import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import notificationService from '../services/notification.service';
import { useNotifications } from '../contexts/NotificationContext';

const PAGE_SIZE = 20;

interface NotificationItem {
  id: number;
  title: string;
  message: string;
  isRead: boolean;
  createdAt: string;
}

const TYPE_OPTIONS = [
  { value: '', label: 'Todos los tipos' },
  { value: 'info', label: 'Info' },
  { value: 'success', label: 'Éxito' },
  { value: 'warning', label: 'Advertencia' },
  { value: 'error', label: 'Error' }
];

const READ_OPTIONS = [
  { value: '', label: 'Todas' },
  { value: 'false', label: 'No leídas' },
  { value: 'true', label: 'Leídas' }
];

const Notifications: React.FC = () => {
  const { markAsRead, markAllAsRead, unreadCount } = useNotifications();
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [type, setType] = useState('');
  const [isRead, setIsRead] = useState('');

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const response = await notificationService.getUserNotifications({
        page,
        limit: PAGE_SIZE,
        ...(type ? { type } : {}),
        ...(isRead !== '' ? { isRead } : {})
      });
      setItems(response.notifications || []);
      setTotalPages(response.pagination?.totalPages || 1);
    } finally {
      setLoading(false);
    }
  }, [isRead, page, type]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const handleMarkAsRead = async (id: number) => {
    await markAsRead(id);
    loadNotifications();
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
    loadNotifications();
  };

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Historial de notificaciones</h1>
            <p className="text-sm text-slate-500">Filtra y revisa eventos operativos del sistema.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/settings/notifications"
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Mis preferencias
            </Link>
            <button
              onClick={handleMarkAllAsRead}
              disabled={unreadCount === 0}
              className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:bg-slate-400"
            >
              Marcar todas leídas
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="space-y-1">
            <span className="text-sm font-medium text-slate-700">Estado</span>
            <select
              value={isRead}
              onChange={(e) => {
                setPage(1);
                setIsRead(e.target.value);
              }}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            >
              {READ_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-1">
            <span className="text-sm font-medium text-slate-700">Tipo</span>
            <select
              value={type}
              onChange={(e) => {
                setPage(1);
                setType(e.target.value);
              }}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            >
              {TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        {loading ? (
          <div className="p-6 text-sm text-slate-500">Cargando notificaciones...</div>
        ) : items.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">No hay notificaciones para los filtros seleccionados.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map((notification) => (
              <li key={notification.id} className={`p-4 ${notification.isRead ? 'bg-white' : 'bg-blue-50/40'}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900">{notification.title}</p>
                    <p className="mt-1 text-sm text-slate-600">{notification.message}</p>
                    <p className="mt-2 text-xs text-slate-400">
                      {new Date(notification.createdAt).toLocaleString('es-DO')}
                    </p>
                  </div>
                  {!notification.isRead && (
                    <button
                      className="rounded-lg border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                      onClick={() => handleMarkAsRead(notification.id)}
                    >
                      Marcar leída
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <button
          className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-50"
          disabled={page <= 1}
          onClick={() => setPage((prev) => Math.max(1, prev - 1))}
        >
          Anterior
        </button>
        <p className="text-sm text-slate-600">
          Página {page} de {totalPages}
        </p>
        <button
          className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-50"
          disabled={page >= totalPages}
          onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
        >
          Siguiente
        </button>
      </div>
    </div>
  );
};

export default Notifications;
